import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TEAL = "#00A8B3";
const NAVY = "#1A1F36";
const MID = "#6B7280";

export const ContactSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill all fields"); return; }
    setDone(true);
    toast.success("Demo request received!");
    const demos = JSON.parse(localStorage.getItem("carecontext_demos") || "[]");
    demos.push({ ...form, timestamp: new Date().toISOString() });
    localStorage.setItem("carecontext_demos", JSON.stringify(demos));
  };

  return (
    <section id="demo" className="py-20 lg:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #F0F8FF 0%, #E0F7FA 50%, #F0F8FF 100%)' }} ref={ref}>
      {/* Decorative bg */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.04, 0.08, 0.04] }} transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-[20%] right-[10%] w-72 h-72 rounded-full" style={{ background: `${TEAL}10` }} />
        <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.03, 0.06, 0.03] }} transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-[15%] left-[8%] w-56 h-56 rounded-full" style={{ background: '#28A74510' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
          <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            className="text-spaced mb-3 block" style={{ color: TEAL }}>G E T &nbsp; S T A R T E D</motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4" style={{ color: NAVY }}>Book a Demo</h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: MID }}>See how CareContext transforms your clinic. Schedule a personalized walkthrough.</p>
          <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.3 }}
            className="flex items-center justify-center gap-2 mt-4">
            <div className="flex -space-x-2">
              {["#00A8B3", "#4CAF50", "#0097A7", "#FF6B6B"].map((c, i) => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white text-[8px] font-bold" style={{ background: c }}>
                  {["PS", "RK", "AM", "DV"][i]}
                </div>
              ))}
            </div>
            <span className="text-xs" style={{ color: MID }}>Join <strong style={{ color: NAVY }}>500+</strong> clinics exploring CareContext</span>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 }} className="max-w-xl mx-auto">
          {/* Animated border card */}
          <div className="relative rounded-2xl p-[2px] overflow-hidden" style={{
            background: `linear-gradient(135deg, ${TEAL}50, transparent 35%, transparent 65%, ${TEAL}50)`,
            backgroundSize: '200% 200%', animation: 'borderShift 5s ease infinite',
          }}>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-2 mb-6">
                <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
                  <Sparkles className="w-5 h-5" style={{ color: TEAL }} />
                </motion.div>
                <h3 className="text-base font-heading font-bold" style={{ color: NAVY }}>Request a Demo</h3>
              </div>

              {done ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
                  <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                    <CheckCircle2 className="w-16 h-16 mx-auto mb-3" style={{ color: TEAL }} />
                  </motion.div>
                  <h3 className="text-lg font-heading font-bold mb-1" style={{ color: NAVY }}>Thank You!</h3>
                  <p className="text-sm mb-4" style={{ color: MID }}>We'll reach out within 24 hours.</p>
                  <Button variant="outline" size="sm" style={{ borderColor: TEAL, color: TEAL }}
                    onClick={() => { setDone(false); setForm({ name:"", email:"", message:"" }); }}>Submit Another</Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {[
                    { id: "name", label: "Full Name", placeholder: "Dr. Priya Sharma", type: "text" },
                    { id: "email", label: "Email", placeholder: "priya@clinic.in", type: "email" },
                  ].map((f, i) => (
                    <motion.div key={f.id} initial={{ opacity: 0, x: -15 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.1 + 0.4 }}>
                      <Label htmlFor={f.id} className="text-xs font-semibold" style={{ color: NAVY }}>{f.label}</Label>
                      <Input id={f.id} type={f.type} placeholder={f.placeholder}
                        value={form[f.id]} onChange={e => setForm(p => ({...p, [f.id]: e.target.value}))}
                        className="mt-1.5 rounded-lg transition-all focus:ring-2"
                        style={{ background: '#F8FBFF', borderColor: '#E5E7EB', '--tw-ring-color': `${TEAL}40` }} />
                    </motion.div>
                  ))}
                  <motion.div initial={{ opacity: 0, x: -15 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.6 }}>
                    <Label htmlFor="msg" className="text-xs font-semibold" style={{ color: NAVY }}>Message</Label>
                    <Textarea id="msg" placeholder="Tell us about your clinic..." rows={3}
                      value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))}
                      className="mt-1.5 rounded-lg resize-none transition-all focus:ring-2"
                      style={{ background: '#F8FBFF', borderColor: '#E5E7EB' }} />
                  </motion.div>
                  <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button type="submit" size="lg" className="w-full btn-teal rounded-lg text-base">
                      <Send className="w-4 h-4 mr-2" />Book Demo
                    </Button>
                  </motion.div>

                  {/* Trust badges */}
                  <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.9 }}
                    className="flex items-center justify-center gap-4 pt-2">
                    {[
                      { icon: "fa-solid fa-lock", label: "SSL Encrypted" },
                      { icon: "fa-solid fa-shield-halved", label: "DPDP Compliant" },
                      { icon: "fa-solid fa-fingerprint", label: "ABDM Verified" },
                    ].map(b => (
                      <div key={b.label} className="flex items-center gap-1.5">
                        <i className={`${b.icon} text-[10px]`} style={{ color: '#4CAF50' }} />
                        <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{b.label}</span>
                      </div>
                    ))}
                  </motion.div>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes borderShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
      `}</style>
    </section>
  );
};
